/**
 * Calendar Parser Utility
 * 
 * Client-side calendar parsing for public bulletin users (localStorage)
 * Supports both ICS/iCalendar and RSS feed formats
 * Uses ical.js library for ICS parsing
 */

import ICAL from 'ical.js'

/**
 * Parse RSS feed content
 */
function parseRSSContent(rssData) {
  try {
    const decodeHtml = (s) => s
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
    
    const extractTag = (block, tag) => {
      const re = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'i')
      const m = block.match(re)
      if (!m) return null
      const cdata = m[1].match(/^<!\[CDATA\[([\s\S]*?)\]\]>$/i)
      return decodeHtml((cdata ? cdata[1] : m[1]).trim())
    }
    
    // Parse time string like "7:40pm" or "7:40pm - 8:40pm"
    const parseTime = (timeStr) => {
      const match = timeStr.match(/(\d{1,2}):(\d{2})(am|pm)/i)
      if (!match) return null
      let hour = parseInt(match[1], 10)
      const minute = parseInt(match[2], 10)
      const isPM = match[3].toLowerCase() === 'pm'
      if (isPM && hour !== 12) hour += 12
      if (!isPM && hour === 12) hour = 0
      return { hour, minute }
    }
    
    // Parse date string like "Wednesday November 5, 2025"
    const parseDateStr = (dateStr) => {
      // Try parsing as-is first
      let d = new Date(dateStr)
      if (!isNaN(d.getTime())) return d
      return null
    }

    const events = []
    let earliestDate = null
    let latestDate = null

    const itemMatches = rssData.matchAll(/<item>([\s\S]*?)<\/item>/gi)
    for (const [, item] of itemMatches) {
      const title = extractTag(item, 'title') || 'Untitled Event'
      const description = extractTag(item, 'description') || ''
      const pubDate = extractTag(item, 'pubDate') || ''
      
      // Try to extract date/time from description first
      const dateMatch = description.match(/(?:Start\s+Date|Date):\s*([^<\n]+)/i)
      const timeMatch = description.match(/(?:Start\s+Time|Time):\s*([^<\n]+)/i)
      const endTimeMatch = description.match(/End\s+Time:\s*([^<\n]+)/i)
      const locationMatch = description.match(/Location:\s*([^<\n]+)/i)
      
      let startDate = null
      let endDate = null
      
      if (dateMatch && timeMatch) {
        // Extract date and time from description
        const dateStr = dateMatch[1].trim().replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim()
        const timeStr = timeMatch[1].trim().replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim()
        
        // Parse time range like "7:40pm - 8:40pm"
        const [startTimeStr, endTimeStrFromRange] = timeStr.includes('-') 
          ? timeStr.split('-').map(s => s.trim()) 
          : [timeStr, null]
        
        const endTimeStr = endTimeMatch 
          ? endTimeMatch[1].trim().replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim()
          : endTimeStrFromRange
        
        // Parse the date
        let parsedDate = parseDateStr(dateStr)
        
        // If date parsing failed, try using pubDate
        if (!parsedDate && pubDate) {
          parsedDate = new Date(pubDate)
          if (isNaN(parsedDate.getTime())) parsedDate = null
        }
        
        if (parsedDate) {
          // Parse start time
          const startTime = parseTime(startTimeStr)
          if (startTime) {
            startDate = new Date(parsedDate)
            startDate.setHours(startTime.hour, startTime.minute, 0, 0)
            
            // Parse end time
            if (endTimeStr) {
              const endTime = parseTime(endTimeStr)
              if (endTime) {
                endDate = new Date(parsedDate)
                endDate.setHours(endTime.hour, endTime.minute, 0, 0)
              } else {
                // Fallback: assume 1 hour duration if we can't parse end time
                endDate = new Date(startDate)
                endDate.setHours(endDate.getHours() + 1)
              }
            } else {
              // No end time specified, assume 1 hour duration
              endDate = new Date(startDate)
              endDate.setHours(endDate.getHours() + 1)
            }
          }
        }
      } else if (pubDate) {
        // Fallback: use pubDate if description parsing fails
        startDate = new Date(pubDate)
        if (!isNaN(startDate.getTime())) {
          endDate = new Date(startDate)
          endDate.setHours(endDate.getHours() + 1)
        }
      }
      
      if (!startDate || isNaN(startDate.getTime())) continue
      
      // Track date range
      if (!earliestDate || startDate < earliestDate) earliestDate = startDate
      if (!latestDate || startDate > latestDate) latestDate = startDate
      
      const eventType = determineEventType(title, description)
      const cleanDescription = description.replace(/<[^>]*>/g, '').replace(/<br\s*\/?>/gi, '\n').trim()
      const cleanLocation = locationMatch 
        ? locationMatch[1].trim().replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '').trim()
        : null
      
      events.push({
        title,
        description: cleanDescription || null,
        start_time: startDate.toISOString(),
        end_time: endDate ? endDate.toISOString() : null,
        location: cleanLocation,
        event_type: eventType,
        uid: pubDate || `${startDate.toISOString()}-${title}`
      })
    }

    if (events.length === 0) {
      throw new Error('No events found in RSS feed')
    }

    // Check if all events are in the past
    const now = new Date()
    const futureEvents = events.filter(e => new Date(e.start_time) > now)

    return {
      success: true,
      events,
      eventsCount: events.length,
      futureEventsCount: futureEvents.length,
      dateRange: {
        earliest: earliestDate?.toISOString(),
        latest: latestDate?.toISOString()
      },
      warning: futureEvents.length === 0 ? 'All events are in the past. Please upload a current calendar.' : null
    }

  } catch (error) {
    console.error('[calendarParser] Error parsing RSS:', error)
    return {
      success: false,
      error: error.message || 'Failed to parse RSS feed',
      events: []
    }
  }
}

/**
 * Parse ICS/iCalendar content
 */
export function parseICSContent(icsData) {
  try {
    // Normalize line endings
    const normalizedData = icsData.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    
    // Detect RSS format
    const isRSS = /<rss|<feed|<item/i.test(normalizedData) && !/BEGIN\s*:\s*VCALENDAR|BEGIN\s*VCALENDAR/i.test(normalizedData)
    
    if (isRSS) {
      return parseRSSContent(normalizedData)
    }

    // Validate ICS format
    if (!normalizedData.includes('BEGIN:VCALENDAR')) {
      throw new Error('Invalid calendar format. Expected iCalendar (.ics) file or RSS feed.')
    }

    const jcalData = ICAL.parse(normalizedData)
    const comp = new ICAL.Component(jcalData)
    const vevents = comp.getAllSubcomponents('vevent')

    const events = []
    let earliestDate = null
    let latestDate = null

    for (const vevent of vevents) {
      const event = new ICAL.Event(vevent)
      
      if (!event.startDate) continue

      const startDate = event.startDate.toJSDate()
      const endDate = event.endDate ? event.endDate.toJSDate() : null

      // Track date range
      if (!earliestDate || startDate < earliestDate) earliestDate = startDate
      if (!latestDate || startDate > latestDate) latestDate = startDate

      // Determine event type
      const summary = event.summary || ''
      const description = event.description || ''
      const eventType = determineEventType(summary, description)

      events.push({
        title: summary || 'Untitled Event',
        description: description || null,
        start_time: startDate.toISOString(),
        end_time: endDate ? endDate.toISOString() : null,
        location: event.location || null,
        event_type: eventType,
        uid: event.uid || `${startDate.toISOString()}-${summary}`
      })
    }

    if (events.length === 0) {
      throw new Error('No events found in calendar')
    }

    // Check if all events are in the past
    const now = new Date()
    const futureEvents = events.filter(e => new Date(e.start_time) > now)

    return {
      success: true,
      events,
      eventsCount: events.length,
      futureEventsCount: futureEvents.length,
      dateRange: {
        earliest: earliestDate?.toISOString(),
        latest: latestDate?.toISOString()
      },
      warning: futureEvents.length === 0 ? 'All events are in the past. Please upload a current calendar.' : null
    }

  } catch (error) {
    console.error('[calendarParser] Error parsing calendar:', error)
    return {
      success: false,
      error: error.message || 'Failed to parse calendar',
      events: []
    }
  }
}

export async function fetchAndParseCalendar(url) {
  try {
    // Normalize webcal
    const normalizedUrl = url.replace(/^webcal:\/\//i, 'https://')

    // Try direct fetch first (may be blocked by CORS)
    try {
      const direct = await fetch(normalizedUrl)
      if (direct.ok) {
        const calendarData = await direct.text()
        // parseICSContent now handles both ICS and RSS
        return parseICSContent(calendarData)
      }
      // If non-OK, fall through to proxy
    } catch (_) {
      // Network/CORS errors fall back to proxy
    }

    // Fallback: call public Edge Function proxy to avoid CORS
    // This proxy supports both ICS and RSS formats
    const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-calendar`
    const proxyRes = await fetch(fnUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY },
      body: JSON.stringify({ calendarUrl: normalizedUrl })
    })
    const data = await proxyRes.json()
    if (!proxyRes.ok || !data.success) {
      throw new Error(data?.error || `Failed to fetch calendar via proxy: HTTP ${proxyRes.status}`)
    }
    // Return same shape as parseICSContent for UI compatibility
    return {
      success: true,
      events: data.events,
      eventsCount: data.eventsCount,
      futureEventsCount: data.futureEventsCount,
      dateRange: data.dateRange,
      warning: data.warning || null
    }

  } catch (error) {
    console.error('[calendarParser] Error fetching calendar:', error)
    return {
      success: false,
      error: error.message || 'Failed to fetch calendar',
      events: []
    }
  }
}

function determineEventType(summary, description) {
  const text = `${summary} ${description}`.toLowerCase()
  
  if (text.includes('tournament') || text.includes('tourney')) return 'tournament'
  if (text.includes('game') || text.includes('vs') || text.includes('vs.') || text.includes('@')) return 'game'
  if (text.includes('practice') || text.includes('ice time') || text.includes('ice slot')) return 'practice'
  
  return 'other'
}

/**
 * Store calendar in localStorage for public (non-logged-in) users
 */
export function storeCalendarInLocalStorage(events, calendarName = 'My Calendar') {
  try {
    const calendarData = {
      events,
      calendarName,
      uploadedAt: new Date().toISOString()
    }
    localStorage.setItem('bb_temp_calendar', JSON.stringify(calendarData))
    return true
  } catch (error) {
    console.error('[calendarParser] Error storing calendar:', error)
    // Check if quota exceeded
    if (error.name === 'QuotaExceededError') {
      return { error: 'Calendar too large to store. Please try a smaller calendar.' }
    }
    return { error: 'Failed to store calendar' }
  }
}

/**
 * Load calendar from localStorage
 */
export function loadCalendarFromLocalStorage() {
  try {
    const data = localStorage.getItem('bb_temp_calendar')
    if (!data) return null
    
    const calendarData = JSON.parse(data)
    return calendarData
  } catch (error) {
    console.error('[calendarParser] Error loading calendar:', error)
    return null
  }
}

/**
 * Clear calendar from localStorage
 */
export function clearCalendarFromLocalStorage() {
  try {
    localStorage.removeItem('bb_temp_calendar')
    return true
  } catch (error) {
    console.error('[calendarParser] Error clearing calendar:', error)
    return false
  }
}

